import dynamic from 'next/dynamic'
import React, {ReactElement, useRef} from 'react'
import styled from 'styled-components'

import {theme as acqusitionHeaderTheme} from '@miro-site/components_acquisition-header'
import Header, {SimplifiedHeader} from '@miro-site/components_header'
import {LandingPageProps, Layout} from '../lib/contentful/types'
import Components from '../utils/components'
import {useContentfulData, Box, LazyImage, toLazyImage} from '@miro-site/core'

interface HeaderRendererProps {
  sections: Layout
  styleSystem?: LandingPageProps['styleSystem']
}

const HeaderWrapper = styled.div`
  position: sticky;
  width: 100%;
  top: 0;
  left: 0;
  z-index: 200;

  &.mobile-menu-opened {
    position: fixed;
    z-index: 700;
  }
`

const HeaderRenderer = ({sections, styleSystem}: HeaderRendererProps): ReactElement => {
  const pageProps = useContentfulData() as LandingPageProps
  const headerWrapperRef = useRef<HTMLDivElement>(null)
  const {header, page} = pageProps
  const firstSection = sections?.[0]
  /**
   * The "normal" header can be loaded in stitches or styled-components.
   */
  if (header) {
    const simplifiedNavbarPageId = '1HqNikg1eh9tvURJMbLH7C'
    const isSimplifiedNavbarPage = page.sys.id === simplifiedNavbarPageId
    const HeaderComponent = isSimplifiedNavbarPage ? SimplifiedHeader : Header
    /**
     * Dynamically loads and return the stitches header for specific pages
     */
    if (styleSystem !== 'twig') {
      //Loading new header for seo pages
      const StandardHeader = dynamic(() => import('@miro-site/features-standard-header'))
      return (
        <StandardHeader renderNavigation containerNode={headerWrapperRef.current} {...header} />
      )
    }
    /**
     * Returns the styled-components header for all other pages
     */
    return (
      <HeaderWrapper ref={headerWrapperRef}>
        <HeaderComponent containerNode={headerWrapperRef.current} {...header} />
      </HeaderWrapper>
    )
  }

  /**
   * If the `header` prop doesn't exists, defaults to using the acquisition header
   */

  // Guess the background for Acquisition Header component based on the first component we will render
  let headerBackground

  if (
    page.fields.acquisitionPage &&
    firstSection &&
    typeof firstSection.values.background === 'string' &&
    acqusitionHeaderTheme.background[firstSection.values.background]
  ) {
    headerBackground = firstSection.values.background
  }
  return (
    <Components.AcquisitionHeader
      background={headerBackground}
      logoClickUrl={page.fields.acquisitionLogoClickUrl}
      customLogo={
        page.fields.acquisitionCustomLogo ? (
          <Box
            $styles={{
              width: page.fields.acquisitionCustomLogo.fields.file.details.image.width,
              height: page.fields.acquisitionCustomLogo.fields.file.details.image.height,
            }}
          >
            <LazyImage
              {...toLazyImage(
                page.fields.acquisitionCustomLogo.fields.file.url,
                page.fields.acquisitionCustomLogo.fields.title,
                null,
                null,
                page.fields.acquisitionCustomLogo.fields.file.contentType,
              )}
            />
          </Box>
        ) : null
      }
    />
  )
}

export default HeaderRenderer
